import { enviroment } from "../enviroment";
import type { ImpersonatedUser } from "./OrganizationImpersonationContext";

// Module-level mirror of the act-as vendor org id.
//
// Why not just the React context: the act-as scope must reach two places the
// context can't — (1) ApiClient, which would create an import cycle
// (ApiClient -> ImpersonationContext -> RepositoriesContext -> repositories
// -> ApiClient), and (2) the PreLoader prefetch wave in App.tsx, which runs
// ABOVE the OrganizationImpersionationProvider in the tree. A plain module
// variable, kept in sync by the provider, serves both without cycles.
//
// Why not read sessionStorage directly on every call: setImpersonation has a
// no-storage fallback (private mode / quota) where impersonation still works
// for the page lifetime — the module variable follows that state, storage
// does not.

// sessionStorage (NOT localStorage) on purpose: a new window/tab always starts
// as the plain admin — impersonation never silently survives into a fresh session.
export const ACT_AS_STORAGE_KEY = "actAsVendor";

export function readStoredImpersonation(): {
  vendorId?: number;
  user?: ImpersonatedUser;
  owner?: string;
} {
  if (!enviroment.actAsVendorEnabled) return {};
  try {
    const raw = sessionStorage.getItem(ACT_AS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed?.vendorId !== "number") return {};
    return { vendorId: parsed.vendorId, user: parsed.user, owner: parsed.owner };
  } catch {
    return {};
  }
}

let actAsOrgId: number | undefined;
let initialized = false;

// Lazy init (not at module load): keeps import order irrelevant and matches
// the provider's own useState initializer, which restores from the same key.
export function getActAsOrgId(): number | undefined {
  if (!initialized) {
    actAsOrgId = readStoredImpersonation().vendorId;
    initialized = true;
  }
  return actAsOrgId;
}

// Cache-key segment for act-as-scoped react-query keys: the impersonated
// vendor org id, or 0 when not impersonating — the segment is ALWAYS present
// so the key shape stays stable (#601's list-key pattern). This is the ONE
// function every logo list/detail key reader and writer must share; a second
// hand-rolled `?? 0` somewhere is how two callers end up disagreeing about
// the same cache entry.
export function actAsCacheKey(): number {
  return getActAsOrgId() ?? 0;
}

// Called synchronously by the provider's setImpersonation so the module value
// can never lag behind the React state within the same tick.
export function setActAsOrgIdStore(vendorId: number | undefined): void {
  actAsOrgId = vendorId;
  initialized = true;
}

export function clearActAsOrgIdStore(): void {
  actAsOrgId = undefined;
  initialized = true;
}
